import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import "@/App.css";
import api from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { SuggestionsProvider } from "@/context/SuggestionsContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { SubscriptionProvider, useSubscription } from "@/context/SubscriptionContext";
import { I18nProvider, LanguageSync } from "@/context/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import { Paywall } from "@/components/Paywall";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Privacy from "@/pages/Privacy";
import Impressum from "@/pages/Impressum";
import Support from "@/pages/Support";
import { CookieConsent } from "@/components/CookieConsent";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Companion from "@/pages/Companion";
import CheckIn from "@/pages/CheckIn";
import Journal from "@/pages/Journal";
import Insights from "@/pages/Insights";
import Habits from "@/pages/Habits";
import Goals from "@/pages/Goals";
import Favorites from "@/pages/Favorites";
import LifeStory from "@/pages/LifeStory";
import Memories from "@/pages/Memories";
import Growth from "@/pages/Growth";
import Community from "@/pages/Community";
import Professionals from "@/pages/Professionals";
import Resources from "@/pages/Resources";
import SupportCircle from "@/pages/SupportCircle";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Guide from "@/pages/Guide";
import Subscription from "@/pages/Subscription";

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-10 w-10 animate-breathe rounded-full bg-primary" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function OnboardingGate({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
}

function PremiumGate({ children, feature }) {
  const { premium, loading } = useSubscription();
  if (loading) return <FullLoader />;
  if (!premium) return <Paywall feature={feature} />;
  return children;
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        setUser(data.user);
        window.history.replaceState(null, "", "/app");
        navigate(data.user.onboarding_completed ? "/app" : "/onboarding", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <FullLoader />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/support" element={<Support />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/app" element={<OnboardingGate><SubscriptionProvider><SuggestionsProvider><NotificationsProvider><Layout /></NotificationsProvider></SuggestionsProvider></SubscriptionProvider></OnboardingGate>}>
        <Route index element={<Dashboard />} />
        <Route path="companion" element={<Companion />} />
        <Route path="guide" element={<Guide />} />
        <Route path="checkin" element={<CheckIn />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="journal" element={<PremiumGate feature="Journaling"><Journal /></PremiumGate>} />
        <Route path="insights" element={<PremiumGate feature="Insights"><Insights /></PremiumGate>} />
        <Route path="habits" element={<PremiumGate feature="Habits"><Habits /></PremiumGate>} />
        <Route path="goals" element={<PremiumGate feature="Goals"><Goals /></PremiumGate>} />
        <Route path="favorites" element={<Favorites />} />
        <Route path="lifestory" element={<PremiumGate feature="Your life story"><LifeStory /></PremiumGate>} />
        <Route path="memories" element={<PremiumGate feature="Memories"><Memories /></PremiumGate>} />
        <Route path="growth" element={<PremiumGate feature="Growth"><Growth /></PremiumGate>} />
        <Route path="community" element={<PremiumGate feature="Community"><Community /></PremiumGate>} />
        <Route path="professionals" element={<Professionals />} />
        <Route path="resources" element={<Resources />} />
        <Route path="circle" element={<PremiumGate feature="Your support circle"><SupportCircle /></PremiumGate>} />
        <Route path="settings" element={<Settings />} />
        <Route path="admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <LanguageSync />
            <AppRouter />
            <CookieConsent />
            <Toaster position="top-center" />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
